import React, { useEffect, useRef, useState } from 'react';
import { Box, Button } from '@mui/material';
import SectorCheckFilter from '../Dropdown/SectorCheckFilter';
import DateTimeRangeFilter from 'components/Dropdown/DateTimeRangeFilter/DateTimeRangeFilter';
import { useLocation, useNavigate } from 'react-router-dom';
import { useHeader } from 'Context/contexts/HeaderTabRightContentContext';
import PageWrapper from 'components/Common/PageWraper';
import TableauLicenses from '../TableauObs/Licenses/Licenses';
import TableauReports from '../TableauObs/Reports/Reports';
import TableauCost from '../TableauObs/Cost/Cost';

const DEFAULT_TIME_RANGE = 'LAST_30_DAYS';
const DEFAULT_SECTOR_LABEL = 'All Sectors';

const TABLEAU_DATE_FIXED_OPTIONS = [
	{ val: '30d', label: 'Last 30 Days' },
	{ val: '60d', label: 'Last 60 Days' },
	{ val: '90d', label: 'Last 90 Days' },
	{ val: '120d', label: 'Last 120 Days' }
];

const TABLEAU_DATE_RANGE_MAP: { [key: string]: string } = {
	'30d': 'LAST_30_DAYS',
	'60d': 'LAST_60_DAYS',
	'90d': 'LAST_90_DAYS',
	'120d': 'LAST_120_DAYS'
};


interface TabPanelProps {
	readonly children?: React.ReactNode;
	readonly index: number;
	readonly value: number;
}

function TabPanel(props: Readonly<TabPanelProps>) {
	const { children, value, index, ...other } = props;

	return (
		<div
			role="tabpanel"
			hidden={value !== index}
			id={`tableau-tabpanel-${index}`}
			aria-labelledby={`tableau-tab-${index}`}
			{...other}
		>
			{value === index && (
				<Box>
					{children}
				</Box>
			)}
		</div>
	);
}

const TableauAdminDashboard: React.FC = () => {
	const { setRightContextContent } = useHeader();
	const location = useLocation();
	const navigate = useNavigate();
	const [tabValue, setTabValue] = useState<number>(
		location.pathname.includes('/tableau/admin/reports') ? 1 :
		location.pathname.includes('/tableau/admin/cost') ? 2 : 0
	);
	const [timeRange, setTimeRange] = useState(DEFAULT_TIME_RANGE);
	const [dateRangeType, setDateRangeType] = useState<string>('30d');
	const [customStartDate, setCustomStartDate] = useState<Date | null>(null);
	const [customEndDate, setCustomEndDate] = useState<Date | null>(null);
	const [selectedSectors, setSelectedSectors] = useState<string[]>([]);
	const [sectorFilterVersion, setSectorFilterVersion] = useState(0);
	const checkedListRef = useRef<any>([]);
	const resetLicenseFiltersRef = useRef<(() => void) | null>(null);

	useEffect(() => {
		if (location.pathname.includes('/tableau/admin/reports')) {
			setTabValue(1);
		} else if (location.pathname.includes('/tableau/admin/cost')) {
			setTabValue(2);
		} else {
			setTabValue(0);
		}
	}, [location.pathname]);

	useEffect(() => {
		if (
			location.pathname === '/tableau/admin' ||
			location.pathname === '/tableau/admin/'
		) {
			navigate('/tableau/admin/dashboard', {
				replace: true
			});
		}
	}, [location.pathname, navigate]);

	const handleDateRangeChange = (type: string, start?: Date, end?: Date) => {
		setDateRangeType(type);
		setCustomStartDate(type === 'custom' ? start ?? null : null);
		setCustomEndDate(type === 'custom' ? end ?? null : null);

		const mappedTimeRange = TABLEAU_DATE_RANGE_MAP[type];

		if (mappedTimeRange) {
			setTimeRange(mappedTimeRange);
		}
	};

	const handleSectorApply = () => {
		setSelectedSectors([...checkedListRef.current]);
	};

	const handleResetFilters = () => {
		setTimeRange(DEFAULT_TIME_RANGE);
		setDateRangeType('30d');
		setCustomStartDate(null);
		setCustomEndDate(null);
		checkedListRef.current = [];
		const globalObject = globalThis as typeof globalThis & { localStorage?: Storage };
		globalObject.localStorage?.removeItem('tableau-admin-sector-filter');
		setSelectedSectors([]);
		setSectorFilterVersion((prev) => prev + 1);

		if (resetLicenseFiltersRef.current) {
			resetLicenseFiltersRef.current();
		}
	};

	useEffect(() => {
		setRightContextContent(
			<Box display="flex" alignItems="center" gap={2}>
				{tabValue !== 2 && (
					<DateTimeRangeFilter
						dateRangeType={dateRangeType}
						customStartDate={customStartDate}
						customEndDate={customEndDate}
						onChange={handleDateRangeChange}
						timezone="CST"
						fixedOptions={TABLEAU_DATE_FIXED_OPTIONS}
						showCustomRange={false}
					/>
				)}

				<Box display='none' sx={{ width: { xs: '100%', sm: 'auto' } }}>
					<SectorCheckFilter
						key={sectorFilterVersion}
						sectorStorageName="tableau-admin-sector-filter"
						handleOkClick={handleSectorApply}
						checkedListRef={checkedListRef}
					/>
				</Box>

				<Button
					variant="pepwiseSecondaryOutline"
					onClick={handleResetFilters}
					sx={{ display: 'none' }}
				>
					Reset Filters
				</Button>
			</Box>
		);

		return () => {
			setRightContextContent(null);
		};
	}, [dateRangeType, customStartDate, customEndDate, tabValue, sectorFilterVersion]);

	return (
		<PageWrapper>
			<Box>
				<TabPanel value={tabValue} index={0}>
					<TableauLicenses  timeRange={timeRange}
                        filters={{
                            sector: checkedListRef.current.length > 0 ? checkedListRef.current.join(',') : 'all',
                            filterOption: timeRange
                        }}
                        checkedListRef={checkedListRef}
						resetFiltersRef={resetLicenseFiltersRef}
				/>
				</TabPanel>
				<TabPanel value={tabValue} index={1}>
					<TableauReports timeRange={timeRange}
                        filters={{
                            sector: checkedListRef.current.length > 0 ? checkedListRef.current.join(',') : 'all',
                            filterOption: timeRange
                        }}
                        checkedListRef={checkedListRef} 
					/>
				</TabPanel>
				<TabPanel value={tabValue} index={2}>
					<TableauCost timeRange={timeRange} selectedSectors={selectedSectors} resetFiltersRef={resetLicenseFiltersRef} />
				</TabPanel>
			</Box>
		</PageWrapper>
	);
};

export default TableauAdminDashboard;